import React, { useEffect, useMemo, useRef, useState } from 'react';
import './profit-calculator.css';

/* Profit calculator for dropshippers.
   Pure client-side math, no API calls. Wrap it in <DropshipperOnly> (see
   DropshipperOnly.jsx) so only dropshippers see it. */

let uid = 0;
const nextId = () => `pc-${++uid}`;

/* ---------- helpers (exported so they can be unit tested) ---------- */

// Keep digits and a single dot, max 2 decimals, max 10 chars.
export function sanitizeMoney(value) {
  let s = String(value === null || value === undefined ? '' : value).replace(/[^\d.]/g, '');
  const dot = s.indexOf('.');
  if (dot !== -1) s = s.slice(0, dot + 1) + s.slice(dot + 1).replace(/\./g, '').slice(0, 2);
  return s.slice(0, 10);
}

export function toNumber(value) {
  const n = parseFloat(value);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

export function formatMoney(n) {
  let r = Math.round((n + Number.EPSILON) * 100) / 100;
  if (r === 0) r = 0; // avoid "-0"
  return r.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

export function formatSigned(n, currency) {
  const rounded = Math.round((n + Number.EPSILON) * 100) / 100;
  return `${rounded < 0 ? '\u2212' : ''}${currency} ${formatMoney(Math.abs(rounded))}`;
}

export function formatPercent(p) {
  const r = Math.round((p + Number.EPSILON) * 10) / 10;
  return `${r < 0 ? '\u2212' : ''}${Math.abs(r).toLocaleString('en-US', { maximumFractionDigits: 1 })}%`;
}

/* All amounts are per unit, in the same currency.
   totalCost   = product price + tax + packing + delivery + other costs
   netSelling  = selling price - sale tax/fees - other deductions (never below 0)
   profit      = netSelling - totalCost
   margin      = profit / selling price      (share of what the customer pays)
   returnOnCost= profit / totalCost          (markup on what you spend) */
export function calculateProfit(v) {
  const totalCost =
    toNumber(v.costPrice) + toNumber(v.tax) + toNumber(v.packing) + toNumber(v.delivery) + toNumber(v.otherCosts);
  const selling = toNumber(v.sellingPrice);
  const netSelling = Math.max(0, selling - toNumber(v.saleTax) - toNumber(v.otherDeductions));
  const hasPrice = selling > 0;
  const profit = hasPrice ? netSelling - totalCost : 0;
  const margin = hasPrice ? (profit / selling) * 100 : 0;
  const returnOnCost = hasPrice && totalCost > 0 ? (profit / totalCost) * 100 : 0;
  const qty = Math.max(1, Math.floor(toNumber(v.qty)) || 1);
  const status = !hasPrice ? 'empty' : profit < 0 ? 'loss' : 'profit';
  return { totalCost, netSelling, profit, margin, returnOnCost, qty, totalProfit: profit * qty, status, hasPrice };
}

// Selling price that gives `percent`% return on cost, after fixed deductions.
export function priceForMarkup(totalCost, percent, saleTax, otherDeductions) {
  return Math.ceil(totalCost * (1 + percent / 100) + toNumber(saleTax) + toNumber(otherDeductions));
}

/* ---------- small pieces ---------- */

function CalcIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="5" y="3" width="14" height="18" rx="2.5" />
      <path d="M8.5 7.5h7M8.5 12h.01M12 12h.01M15.5 12h.01M8.5 16h.01M12 16h.01M15.5 16h.01" />
    </svg>
  );
}

function Chevron() {
  return (
    <svg className="pc__chevron" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

function MoneyField({ id, label, value, onChange, currency, required, placeholder = '0', hint, wide }) {
  return (
    <div className={'pc-field' + (wide ? ' pc-field--wide' : '')}>
      <label htmlFor={id}>
        {label}
        {required ? <span className="pc-field__req" aria-hidden="true"> *</span> : null}
      </label>
      <div className="pc-input">
        <span className="pc-input__unit" aria-hidden="true">{currency}</span>
        <input
          id={id}
          type="text"
          inputMode="decimal"
          autoComplete="off"
          value={value}
          placeholder={placeholder}
          aria-required={required ? 'true' : undefined}
          aria-describedby={hint ? `${id}-hint` : undefined}
          onChange={(e) => onChange(sanitizeMoney(e.target.value))}
        />
      </div>
      {hint ? <span id={`${id}-hint`} className="pc-field__hint">{hint}</span> : null}
    </div>
  );
}

/* ---------- main component ---------- */

export default function ProfitCalculator({
  costPrice = 0,          // product price the dropshipper pays (pass the current/variant price)
  defaultQty = 50,        // pcs used for "Estimated profit" (e.g. the product's minimum order quantity)
  defaultPacking = 0,
  defaultDelivery = 0,
  markupPresets = [20, 30, 50],
  defaultOpen = true,
  currency = 'Rs',
  className = '',
}) {
  const idRef = useRef(null);
  if (idRef.current === null) idRef.current = nextId();
  const base = idRef.current;

  const [open, setOpen] = useState(defaultOpen);
  const [cost, setCost] = useState(sanitizeMoney(costPrice));
  const costTouched = useRef(false);
  const [tax, setTax] = useState('');
  const [packing, setPacking] = useState(defaultPacking ? String(defaultPacking) : '');
  const [delivery, setDelivery] = useState(defaultDelivery ? String(defaultDelivery) : '');
  const [otherCosts, setOtherCosts] = useState('');
  const [price, setPrice] = useState('');
  const [saleTax, setSaleTax] = useState('');
  const [deductions, setDeductions] = useState('');
  const [qty, setQty] = useState(String(defaultQty));

  // Follow the product/variant price until the user edits it themselves.
  useEffect(() => {
    if (!costTouched.current) setCost(sanitizeMoney(costPrice));
  }, [costPrice]);

  const result = useMemo(
    () => calculateProfit({ costPrice: cost, tax, packing, delivery, otherCosts, sellingPrice: price, saleTax, otherDeductions: deductions, qty }),
    [cost, tax, packing, delivery, otherCosts, price, saleTax, deductions, qty]
  );

  function reset() {
    costTouched.current = false;
    setCost(sanitizeMoney(costPrice));
    setTax('');
    setPacking(defaultPacking ? String(defaultPacking) : '');
    setDelivery(defaultDelivery ? String(defaultDelivery) : '');
    setOtherCosts('');
    setPrice('');
    setSaleTax('');
    setDeductions('');
    setQty(String(defaultQty));
  }

  const profitText = result.status === 'empty' ? `${currency} 0` : formatSigned(result.profit, currency);
  const message =
    result.status === 'empty'
      ? 'Enter your selling price to see your profit.'
      : result.status === 'loss'
      ? 'This price is below your total cost.'
      : `You earn ${formatSigned(result.profit, currency)} on every unit sold.`;

  return (
    <section className={`pc ${className}`.trim()} aria-labelledby={`${base}-title`}>
      <header className="pc__head">
        <span className="pc__icon"><CalcIcon /></span>
        <div className="pc__heading">
          <h3 id={`${base}-title`} className="pc__title">Profit calculator</h3>
          <p className="pc__sub">Work out your earnings before you list this product.</p>
        </div>
        <span className="pc__badge">Amounts in {currency}</span>
        <button
          type="button"
          className="pc__toggle"
          aria-expanded={open}
          aria-controls={`${base}-body`}
          aria-label={open ? 'Collapse profit calculator' : 'Expand profit calculator'}
          onClick={() => setOpen((v) => !v)}
          data-open={open ? 'true' : 'false'}
        >
          <Chevron />
        </button>
      </header>

      <div id={`${base}-body`} className="pc__body" hidden={!open}>
        <fieldset className="pc__group">
          <legend>Your cost per unit</legend>
          <div className="pc__grid">
            <MoneyField id={`${base}-cost`} label="Product price" wide currency={currency} value={cost}
              onChange={(v) => { costTouched.current = true; setCost(v); }} />
            <MoneyField id={`${base}-tax`} label="Tax, if applicable" currency={currency} value={tax} onChange={setTax} />
            <MoneyField id={`${base}-packing`} label="Packing & labeling" currency={currency} value={packing} onChange={setPacking} />
            <MoneyField id={`${base}-delivery`} label="Delivery charges" currency={currency} value={delivery} onChange={setDelivery} />
            <MoneyField id={`${base}-other`} label="Other costs" currency={currency} value={otherCosts} onChange={setOtherCosts} />
          </div>
        </fieldset>

        <fieldset className="pc__group">
          <legend>Your selling price per unit</legend>
          <div className="pc__grid">
            <MoneyField id={`${base}-price`} label="Selling price" required wide currency={currency} value={price} onChange={setPrice} placeholder="Enter price" />
            <MoneyField id={`${base}-saletax`} label="Sales tax or fees" currency={currency} value={saleTax} onChange={setSaleTax} />
            <MoneyField id={`${base}-deduct`} label="Other deductions" currency={currency} value={deductions} onChange={setDeductions} />
          </div>

          {markupPresets.length > 0 && (
            <div className="pc__quick">
              <span className="pc__quick-label" id={`${base}-quick`}>Quick price, markup on cost</span>
              <div className="pc__quick-list" role="group" aria-labelledby={`${base}-quick`}>
                {markupPresets.map((p) => (
                  <button
                    key={p}
                    type="button"
                    className="pc__chip"
                    disabled={result.totalCost === 0}
                    onClick={() => setPrice(sanitizeMoney(priceForMarkup(result.totalCost, p, saleTax, deductions)))}
                  >
                    +{p}%
                  </button>
                ))}
              </div>
            </div>
          )}
        </fieldset>

        <div className="pc__totals">
          <div className="pc__total">
            <span>Total cost</span>
            <strong>{currency} {formatMoney(result.totalCost)}</strong>
          </div>
          <div className="pc__total">
            <span>Net selling price</span>
            <strong>{currency} {formatMoney(result.netSelling)}</strong>
          </div>
        </div>

        <div className={`pc__result pc__result--${result.status}`} role="region" aria-label="Profit insights">
          <div className="pc__result-main">
            <span className="pc__result-label">Profit per unit</span>
            <strong className="pc__result-value" aria-live="polite">{profitText}</strong>
            <span className="pc__result-note">{message}</span>
          </div>

          <div className="pc__stats">
            <div className="pc__stat">
              <span>Margin</span>
              <strong>{result.hasPrice ? formatPercent(result.margin) : '0%'}</strong>
              <small>of selling price</small>
            </div>
            <div className="pc__stat">
              <span>Return on cost</span>
              <strong>{result.hasPrice ? formatPercent(result.returnOnCost) : '0%'}</strong>
              <small>profit vs. what you spend</small>
            </div>
          </div>

          <div className="pc__est">
            <label htmlFor={`${base}-qty`}>Estimated profit for</label>
            <input
              id={`${base}-qty`}
              className="pc__qty"
              type="text"
              inputMode="numeric"
              autoComplete="off"
              value={qty}
              onChange={(e) => setQty(e.target.value.replace(/\D/g, '').slice(0, 5))}
            />
            <span className="pc__est-pcs">pcs</span>
            <strong className="pc__est-value">
              {result.status === 'empty' ? `${currency} 0` : formatSigned(result.totalProfit, currency)}
            </strong>
          </div>
        </div>

        <div className="pc__foot">
          <p>Estimates only. Real earnings also depend on returns, courier charges and marketplace fees.</p>
          <button type="button" className="pc__reset" onClick={reset}>Reset</button>
        </div>
      </div>
    </section>
  );
}
